import { GridActionsCellItem } from "@mui/x-data-grid";
import { useState } from "react";
import { AiOutlinePlus } from "react-icons/ai";
import { BiDotsHorizontalRounded } from "react-icons/bi";
import useEditFormStore from "../../../../../state-management/useEditFormStore";
import { Columns } from "./data";
import TeamMemberModalForm from "./modal/TeamMemberModalForm";
import useUserStore from "../../../../../state-management/useUserStore";
import { APP_CONFIG } from "../../../../../helpers/appHelpers";
import CustomPagination from "../../../../../components/table/CustomPagination";
import APISearchInput from "../../../../../components/search/APISearchInput";
import { USER_CATEGORY, UserDto } from "../../../../../models/user.dto";
import {
	useGetUserSearch,
	useGetUser,
} from "../../../../../services/user.service";
import { useGetOrganizationRole } from "../../../../../services/organization-role.service";
import { OrganizationRoleOrderBy } from "../../../../../models/organization-role.dto";
import useCompanyStore from "../../../../../state-management/useCompanyStore";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import UpdateAccountConfigModal from "../../../../../components/modals/UpdateAccountConfigModal";
import { PATHS } from "../../../../../routes/routes.paths";
import CustomStyledTable from "../../../../../components/table/CustomStyledTable";
import { useManageTableWithStateVariables } from "../../../../../helpers/useManageTable";
import { Popover } from "@mui/material";
import { BsTrash } from "react-icons/bs";
import { LuEye, LuActivitySquare } from "react-icons/lu";
import { BiRecycle } from "react-icons/bi";
import { MdBlock } from "react-icons/md";
import TeamMemberActionModal from "./modal/TeamMemberActionModal";
import API from "../../../../../services/utils/api";
import { useAsyncEffect, useLockFn } from "ahooks";
import {
	useCheckUserPermissions,
	PermissionChecker,
	PERMISSIONS,
} from "../../../../../helpers/usePermissionManager";
import {
	accountUsersColumns,
	accountUsersColumnVisibility,
} from "./tanstack-columns";
import { TableId } from "../../../../../models/table-configuration.dto";
import TanStackEmployeeTable from "../../../../../components/table/TanStackEmployeeTable";
import { useColumnVisibilityModel } from "../../../../../helpers/useColumnVisibilityModel";

type ActionMode = "REACTIVATE" | "DISABLE" | "RESEND_EMAIL" | "DELETE";

const TeamMembers = () => {
	const navigate = useNavigate();
	const { currentUser } = useUserStore();
	const { savedCompanies } = useCompanyStore();
	const userOrganization = currentUser.user.organization;
	const { checkUserPermissionsAnd } = useCheckUserPermissions();
	const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
	const [openModal, setOpenModal] = useState(false);
	const [isEditForm, setIsEditForm] = useState(false);
	const { formDataValues, setFormData } = useEditFormStore();
	const [openActionsModal, setOpenActionsModal] = useState(false);
	const [actionMode, setActionMode] = useState<ActionMode>("DISABLE");
	const [openUpgradeModal, setOpenUpgradeModal] = useState(false);
	const [upgradeModalContent, setUpgradeModalContent] = useState("");
	const [totalTeamMembers, setTotalTeamMembers] = useState(0);
	const [onClickActionButton, setOnClickActionButton] = useState<
		(() => void) | null
	>(null);
	const highestSubscriptionTierId = sessionStorage.getItem(
		"highestSubscriptionTierId",
	);
	const { tableKey, columnVisibilityModel, setColumnVisibilityModel } =
		useColumnVisibilityModel(accountUsersColumnVisibility);

	const { mutate: searchTeamMember } = useGetUserSearch();
	const {
		tableAPIRef,
		defaultTableData,
		tableData,
		isLoading,
		page,
		pageCount,
		setTableData,
		setSearching,
		addNewData,
		reloadData,
		handlePageChange,
	} = useManageTableWithStateVariables<UserDto>({
		fetchHook: useGetUser,
		options: {
			organizationId: currentUser.user.organizationId,
			category: USER_CATEGORY.OPERATIONS,
		},
	});

	useAsyncEffect(
		useLockFn(async () => {
			try {
				const { data } = await API.UserAPI.getUserCount({
					organizationId: currentUser.user.organizationId,
				});
				setTotalTeamMembers(data?.count);
			} finally {
			}
		}),
		[],
	);

	const { data: organizationRoles } = useGetOrganizationRole({
		organizationId: currentUser.user.organizationId,
		active: true,
		page: 1,
		limit: APP_CONFIG.PAGE_LIMIT,
		orderBy: OrganizationRoleOrderBy.NAME_ASC,
	});

	const handleClose = () => setAnchorEl(null);
	const handleOpenModal = () => {
		if (!userOrganization?.config) {
			toast.info(
				"Organization configurations not found! Kindly sign out and sign back in to fix this.",
			);
			return;
		}

		const maxUsers = userOrganization?.config.maxUsers as number;

		if (maxUsers < 0 || maxUsers > totalTeamMembers) {
			setAnchorEl(null);
			setIsEditForm(false);
			setOpenModal(true);
			return;
		}

		if (userOrganization?.billingType.name.toLowerCase() === "contract") {
			setOpenUpgradeModal(true);
			// setOnClickActionButton(() => navigate(-1));
			setUpgradeModalContent(
				"Maximum team member limit reached! Kindly contact the Akatua team to increase limit.",
			);
			return;
		}

		if (highestSubscriptionTierId === userOrganization?.subsciptionTier.id) {
			setOpenUpgradeModal(true);
			// setOnClickActionButton(() => navigate(-1));
			setUpgradeModalContent(`Maximum team member limit reached! Kindly contact the Akatua team to increase 
				limit as you are currently on the highest subscription package.`);
			return;
		}

		setOpenUpgradeModal(true);
		setOnClickActionButton(() =>
			navigate(PATHS.CREATE_PAYROLL_COMPANY + "?upgradeRequired=true"),
		);
		setUpgradeModalContent(
			"Maximum team member limit reached! Upgrade to higher subscription package to continue.",
		);
	};

	const additionalColumns = [
		{
			field: "actions",
			type: "actions",
			headerName: "Actions",
			minWidth: 150,
			flex: 1,
			headerClassName: "super-app-theme--header",
			cellClassName: "actions ",
			getActions: (params: any) => {
				return [
					<GridActionsCellItem
						icon={<BiDotsHorizontalRounded />}
						label="actions"
						onClick={(e: any) => {
							// console.log(params.row, "params");
							setFormData(params?.row);
							setAnchorEl(e.currentTarget);
						}}
					/>,
				];
			},
		},
	];

	const columnsWithAdditions = Columns.map(
		(obj) => additionalColumns.find((o) => o.field === obj.field) || obj,
	);

	return (
		<div>
			<h2 className="text-2sm font-semibold mb-3">Team Members</h2>
			<hr />
			<div className="flex items-center justify-between mt-7 mb-8">
				<APISearchInput
					fallbackData={defaultTableData}
					setTableData={setTableData}
					mutate={searchTeamMember}
					setSearching={setSearching}
					popupTable
				/>
				<PermissionChecker permissions={[PERMISSIONS.USERS_WRITE]}>
					<div
						className="flex items-center text-primary-blue text-sm cursor-pointer w-fit"
						onClick={handleOpenModal}
					>
						<AiOutlinePlus className="mr-1 text-sm" />
						Add New Team Member
					</div>
				</PermissionChecker>
			</div>
			<TanStackEmployeeTable
				ref={tableAPIRef}
				tableId={TableId.TEAM_MEMBERS}
				data={tableData}
				columns={accountUsersColumns}
				columnVisibility={columnVisibilityModel}
				onColumnVisibilityChange={setColumnVisibilityModel}
				loading={isLoading}
				onRowClick={(row, event) => {
					setFormData(row);
					setAnchorEl(event.currentTarget);
				}}
				onActionClick={(row, event) => {
					setFormData(row);
					setAnchorEl(event.currentTarget);
				}}
				initialColumnVisibility={accountUsersColumnVisibility}
				pageSize={10}
				pageCount={pageCount}
				apiRef={tableAPIRef}
			/>
			{/* <CustomStyledTable
				rows={tableData}
				columns={columnsWithAdditions}
				apiRef={tableAPIRef}
				loading={isLoading}
				pageCount={pageCount}
				pagination={
					<CustomPagination
						pageCount={pageCount}
						pageState={page}
						handleChange={handlePageChange}
					/>
				}
				setAnchorEl={
					checkUserPermissionsAnd([PERMISSIONS.USERS_WRITE])
						? setAnchorEl
						: undefined
				}
				hasPermission={checkUserPermissionsAnd([PERMISSIONS.USERS_READ])}
			/> */}
			<Popover
				id={anchorEl ? "simple-popover" : undefined}
				open={Boolean(anchorEl)}
				anchorEl={anchorEl}
				onClose={handleClose}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "left",
				}}
				PaperProps={{
					style: {
						width: "auto",
						borderRadius: 8,
						display: "flex",
						justifyContent: "center",
					},
				}}
			>
				<div className="flex flex-col justify-center p-3 text-secondary-darkgray">
					<div
						onClick={() => {
							setIsEditForm(true);
							setOpenModal(true);
							handleClose();
						}}
						className="table-action-popover"
					>
						<LuEye />
						<p className="text-2sm">View Details</p>
					</div>
					{formDataValues?.status === "ACTIVE" && (
						<div
							onClick={() => {
								setActionMode("DISABLE");
								setOpenActionsModal(true);
								handleClose();
							}}
							className="table-action-popover"
						>
							<MdBlock />
							<p className="text-2sm">Disable Account</p>
						</div>
					)}
					{formDataValues?.status === "DISABLED" && (
						<div
							onClick={() => {
								setActionMode("REACTIVATE");
								setOpenActionsModal(true);
								handleClose();
							}}
							className="table-action-popover"
						>
							<LuActivitySquare />
							<p className="text-2sm">Reactivate Account</p>
						</div>
					)}
					{formDataValues?.status === "INACTIVE" && (
						<div
							onClick={() => {
								setActionMode("RESEND_EMAIL");
								setOpenActionsModal(true);
								handleClose();
							}}
							className="table-action-popover"
						>
							<BiRecycle />
							<p className="text-2sm">Resend Account Confirmation Email</p>
						</div>
					)}
					{formDataValues?.status === "INACTIVE" && (
						<div
							onClick={() => {
								setActionMode("DELETE");
								setOpenActionsModal(true);
								handleClose();
							}}
							className="table-action-popover"
						>
							<BsTrash />
							<p className="text-2sm">Delete Account</p>
						</div>
					)}
				</div>
			</Popover>
			{openModal && (
				<TeamMemberModalForm
					open={openModal}
					setOpen={setOpenModal}
					title={
						isEditForm
							? `Update ${formDataValues?.firstName} ${formDataValues?.lastName}`
							: "Add New Team Member"
					}
					isEditForm={isEditForm}
					tableAPIRef={tableAPIRef}
					organizationRoles={
						organizationRoles?.data?.filter(
							(role) => role.category !== "HR" && role.category !== "EMPLOYEE",
						) || []
					}
					companies={savedCompanies}
					addNewData={addNewData}
				/>
			)}

			{openActionsModal && (
				<TeamMemberActionModal
					mode={actionMode}
					open={openActionsModal}
					setOpen={setOpenActionsModal}
					tableAPIRef={tableAPIRef}
					reloadData={reloadData}
				/>
			)}

			<UpdateAccountConfigModal
				open={openUpgradeModal}
				content={upgradeModalContent}
				handleClose={() => setOpenUpgradeModal(false)}
				onClickActionButton={onClickActionButton}
			/>
		</div>
	);
};

export default TeamMembers;
